<button
    type="button"
    class="content cipo--btn-reset"
    [disabled]="isLoadingTree()"
    [attr.aria-expanded]="isContextOverlayOpen()"
    [attr.aria-label]="'a11y.contextView.trigger' | translate"
    aria-haspopup="dialog"
    (click)="toggleContextOverlay()"
    #contextContent>
    @if (isLoadingTree()) {
        <ngx-skeleton-loader
            class="cipo--width--full"
            [theme]="{ height: '12px', margin: 0, display: 'flex' }"></ngx-skeleton-loader>
    } @else {
        @if (context()) {
            <ng-container *ngTemplateOutlet="contextPart; context: { $implicit: lastContextPart() }"></ng-container>
        } @else if (!context() && contractsNumberWithCurrentFilter() > 0) {
            <span>{{ "HIERARCHY_CONTEXT.NO_CONTRACT_SELECTED" | translate }}</span>
        } @else if (!context() && contractsNumberWithoutCurrentFilter() > 0) {
            <span>{{ "HIERARCHY_CONTEXT.NO_ACTIVE_CONTRACTS" | translate }}</span>
        } @else if (contractsNumberWithoutCurrentFilter() === 0) {
            <span>{{ "HIERARCHY_CONTEXT.NO_CONTRACTS" | translate }}</span>
        }
        <mat-icon class="arrow-down" aria-hidden="true">arrow_drop_down</mat-icon>
    }
</button>

<ng-template
    cdkConnectedOverlay
    cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
    [cdkConnectedOverlayOrigin]="contextContentOrigin()"
    [cdkConnectedOverlayOpen]="isContextOverlayOpen()"
    [cdkConnectedOverlayHasBackdrop]="true"
    (detach)="isContextOverlayOpen.set(false)"
    (backdropClick)="isContextOverlayOpen.set(false)">
    <div class="cipo overlay-content" [style.width.px]="hostWidth() + 20"
        role="dialog"
        [attr.aria-label]="'a11y.contextView.overlay' | translate"
        cdkTrapFocus
        [cdkTrapFocusAutoCapture]="true"
        (keydown.escape)="$event.stopPropagation(); onEscape()">
        <div class="context-tree-header">
            <mat-form-field class="cipo-input cipo-input--sm">
                <input
                    matInput
                    type="text"
                    #searchInput
                    [formControl]="searchControl"
                    [attr.aria-label]="'a11y.contextView.search' | translate"
                    [placeholder]="'general.searchWaiting' | translate"
                    data-testid="context-view-search" />
                @if (searchControl.value) {
                    <button matSuffix mat-icon-button [attr.aria-label]="'a11y.form.clearInput' | translate" (click)="searchControl.setValue('')">
                        <mat-icon aria-hidden="true">close</mat-icon>
                    </button>
                }
            </mat-form-field>
            <mat-form-field class="cipo-input cipo-input--sm">
                <mat-icon matPrefix aria-hidden="true">filter_alt</mat-icon>
                <mat-select
                    (selectionChange)="setShowType($event.value)"
                    [value]="showType()"
                    [attr.aria-label]="'a11y.contextView.filterBy' | translate">
                    <mat-option [value]="SHOW_TYPE.Active">
                        {{ "HIERARCHY_CONTEXT.ACTIVE" | translate }}
                    </mat-option>
                    <mat-option [value]="SHOW_TYPE.OnlyClosed">
                        {{ "HIERARCHY_CONTEXT.ONLY_CLOSED" | translate }}
                    </mat-option>
                    <mat-option [value]="SHOW_TYPE.OnlyArchived">
                        {{ "HIERARCHY_CONTEXT.ONLY_ARCHIVED" | translate }}
                    </mat-option>
                    <mat-option [value]="SHOW_TYPE.All">
                        {{ "HIERARCHY_CONTEXT.ALL" | translate }}
                    </mat-option>
                </mat-select>
            </mat-form-field>
        </div>
        <div class="context-tree-container scrollable m-t" #contextScrollContainer>
            @if (isLoadingTree()) {
                @for (i of loadingTreeSkeletonArray; track i) {
                    <div class="context-tree-skeleton">
                        <ngx-skeleton-loader
                            [theme]="{ height: '46px', margin: 0, display: 'flex' }"></ngx-skeleton-loader>
                    </div>
                }
            } @else {
                <mat-tree [dataSource]="dataSource" [treeControl]="treeControl" [attr.aria-label]="'a11y.contextView.tree' | translate">
                    <mat-tree-node
                        *matTreeNodeDef="let node"
                        matTreeNodePadding
                        matTreeNodePaddingIndent="24"
                        #contextNodeElement
                        [class.selected]="node.selected"
                        [class.cipo--cursor--default]="!(ignoreContextPermission() || node.context.hasPermission)"
                        [class.cipo--cursor--pointer]="ignoreContextPermission() || node.context.hasPermission"
                        (click)="nodeClick(node)"
                        (keydown.enter)="nodeClick(node)"
                        (keydown.space)="$event.preventDefault(); nodeClick(node)">
                        <div class="context-node">
                            <ng-container
                                *ngTemplateOutlet="contextPart; context: { $implicit: node.context }"></ng-container>
                        </div>
                    </mat-tree-node>
                    <mat-tree-node
                        *matTreeNodeDef="let node; when: hasChild"
                        matTreeNodePadding
                        matTreeNodePaddingIndent="24"
                        #contextNodeElement
                        [class.sticky-level0]="node.level === 0"
                        [class.sticky-level1]="node.level === 1"
                        [class.selected]="node.selected"
                        [class.cipo--cursor--default]="!(ignoreContextPermission() || node.context.hasPermission)"
                        [class.cipo--cursor--pointer]="ignoreContextPermission() || node.context.hasPermission"
                        (click)="nodeClick(node)"
                        (keydown.enter)="nodeClick(node)"
                        (keydown.space)="$event.preventDefault(); nodeClick(node)">
                        <div class="context-node">
                            <ng-container
                                *ngTemplateOutlet="contextPart; context: { $implicit: node.context }"></ng-container>
                        </div>
                    </mat-tree-node>
                </mat-tree>
                @if (!dataSource.data.length) {
                    <div class="context-tree-empty">
                        <mat-icon aria-hidden="true">search</mat-icon>
                        <span>{{ "HIERARCHY_CONTEXT.NO_CONTRACTS_FOUND" | translate }}</span>
                    </div>
                }
            }
        </div>
    </div>
</ng-template>

<ng-template #contextPart let-part>
    @if (part?.id) {
        <itv-module-icon [iconid]="part.moduleIconId" [color]="part.moduleColor"></itv-module-icon>
        @if (part.isArchived) {
            <span class="archivedContract">archived</span>
        } @else if (part.isClosed) {
            <span class="closedContract">closed</span>
        }
        <span data-testid="context-view-part">
            {{ part.no }}{{ part.no ? " - " : "" }}{{ part.name ?? part.moduleAbbr }}
        </span>
    }
</ng-template>
